/**
 * Icon colours, as kit tokens (#726). Icons are painted into images, which cannot read CSS variables, so a recipe
 * names the token it wants and is painted with that token's value, repeated here from kit.css (`contrast.test.ts`
 * keeps the two equal). Only white and black, as washes, are written as themselves.
 */
export const TOKENS = {
  "foreground": "#252220",
  "cell-foreground": "#504e4c",
  "muted-foreground": "#696765",
  "subtle-foreground": "#888682",
  "kbd-foreground": "#5d5a58",
  "icon-fill": "#dbd9d6",
  "icon-inner": "#f8f7f6",
  "muted": "#f6f5f3",
  "border": "#dedcd9",
  "card": "#ffffff",
  "chart-grid": "#f1f0ee",
  "chart-active": "#364e6e",
  "success": "#1c7b5a",
  "success-tint": "#e1f8ec",
  "warning": "#906106",
  "warning-tint": "#feefdb",
  "destructive": "#a01d2a",
  "destructive-tint": "#ffeceb",
  "info": "#5f564d",
  "info-tint": "#f4f0ed",
  "brand-mark": "#5c799f",
  "brand-tint": "#eaf1f9",
  "brand-tint-strong": "#cedcef",
} as const;

export type Token = keyof typeof TOKENS;
export type Colour = Token | "white" | "black";

export const FOREGROUND: Colour = "foreground";
export const INK: Colour = "cell-foreground";
export const MUTED_INK: Colour = "muted-foreground";
export const SUBTLE: Colour = "subtle-foreground";
export const KBD: Colour = "kbd-foreground";
export const TONE: Colour = "icon-fill";
export const INNER: Colour = "icon-inner";
export const MUTED: Colour = "muted";
export const BORDER: Colour = "border";
export const CHART_GRID: Colour = "chart-grid";
export const CHART_ACTIVE: Colour = "chart-active";
export const SUCCESS: Colour = "success";
export const SUCCESS_TINT: Colour = "success-tint";
export const WARNING: Colour = "warning";
export const WARNING_TINT: Colour = "warning-tint";
export const DESTRUCTIVE: Colour = "destructive";
export const DESTRUCTIVE_TINT: Colour = "destructive-tint";
export const BRAND: Colour = "brand-mark";
export const BRAND_TINT: Colour = "brand-tint";
export const BRAND_TINT_STRONG: Colour = "brand-tint-strong";
export const INFO: Colour = "info";
export const INFO_TINT: Colour = "info-tint";
export const PAPER: Colour = "card";
export const WHITE: Colour = "white";
export const BLACK: Colour = "black";

/** The brand paint: a neutral duotone recoloured, ink to brand and its fills to the brand tints. */
export const BRAND_PAINT: Readonly<Partial<Record<Colour, Colour>>> = {
  [INK]: BRAND,
  [TONE]: BRAND_TINT_STRONG,
  [INNER]: BRAND_TINT,
  [MUTED]: BRAND_TINT,
};
